import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  UserEntity,
  UserType,
  useLazyFetchUserProfileQuery,
  useFetchManyUsersQuery,
  useImportUserProfilesMutation,
  useImportSampleProfilesMutation,
  AdminRole,
} from 'src/store/userApi';
import { selectUser } from 'src/store/userSlice';

import { Tabs } from 'src/ui/Tabs';
import { Avatar } from 'src/ui/Avatar';
import { GenderIcon } from 'src/ui/Avatar';
import { UserTypeBadge } from 'src/ui/Badge';
import { Icon } from 'src/ui/Icon';
import { Button } from 'src/ui/Button';
import { Modal } from 'src/ui/Modal';
import { Alert } from 'src/ui/Alert';
import { useToast } from 'src/ui/Toast';
import { ProgressToast } from 'src/ui/ProgressToast';
import {
  UserSearchPanel,
  UserPanelFilters,
} from 'src/ui/patterns/UserSearchPanel';
import { defaultUserFilterGroups } from 'src/ui/patterns/userFiltersConfig';
import { toPanelUser } from 'src/ui/patterns/userAdapter';
import { buildUsersQuery } from 'src/ui/patterns/userFilterMapping';

import UserProfileEditor from './userProfileEditor';
import UserPublicProfile from './userPublicProfile';
import UserSuggestions from './userSuggestions';
import UserConversationHistory from './userConversationHistory';
import UserVectorProfile from './userVectorProfile';
import UserQrCode from './userQrCode';

const IS_PROD = process.env.REACT_APP_BUILD_ENV === 'production';

const TABS = [
  { id: 'profile', label: 'Profile', icon: 'user' as const },
  { id: 'public', label: 'Public Profile', icon: 'eye' as const },
  { id: 'suggestions', label: 'Suggestions', icon: 'heart' as const },
  {
    id: 'history',
    label: 'Conversation History',
    icon: 'messages-square' as const,
  },
  { id: 'vector', label: 'Vector Profile', icon: 'hash' as const },
  { id: 'qr', label: 'QR Code', icon: 'qr-code' as const },
];

const UserProfilePage = () => {
  const [selectedUser, setSelectedUser] = useState<UserEntity | null>(null);
  const [activeTab, setActiveTab] = useState('profile');
  const { userId } = useParams<{ userId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  const [fetchUserProfile] = useLazyFetchUserProfileQuery();
  const toast = useToast();

  // Import/export confirmation state
  const [pendingImportUsers, setPendingImportUsers] = useState<any[] | null>(
    null
  );
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const [pendingSampleCreate, setPendingSampleCreate] = useState<{
    users: any[];
    images: File[];
  } | null>(null);
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sampleInputRef = useRef<HTMLInputElement>(null);

  const [importUserProfiles, importUserProfilesStatus] =
    useImportUserProfilesMutation();
  const [importSampleProfiles, importSampleProfilesStatus] =
    useImportSampleProfilesMutation();

  // Running indicator for the long, synchronous create/import operations.
  const [opInfo, setOpInfo] = useState<{
    title: string;
    subtitle: string;
  } | null>(null);
  const [opProgress, setOpProgress] = useState(0);
  const opLoading =
    importSampleProfilesStatus.isLoading || importUserProfilesStatus.isLoading;

  const isAdmin =
    currentUser?.adminRole && currentUser.adminRole >= AdminRole.ADMIN;

  // Server caps pagination at 100 rows per page.
  const PAGE_SIZE = 100;
  const [filters, setFilters] = useState<UserPanelFilters>({
    search: '',
    selections: {},
    ageMin: '',
    ageMax: '',
    includeDeleted: false,
  });
  const [offset, setOffset] = useState(0);

  // Backend query built from the panel's filters + current page window.
  const usersQuery = useMemo(
    () => buildUsersQuery(filters, offset, PAGE_SIZE),
    [filters, offset]
  );

  const {
    data: usersData,
    isFetching: usersFetching,
    refetch: refetchUsers,
    error,
  } = useFetchManyUsersQuery(usersQuery);
  const rawUsers = usersData?.data ?? [];
  const panelUsers = rawUsers.map(toPanelUser);
  // True total from the server (counts all matching rows, not just this page).
  const totalUsers = usersData?.meta?.total;
  const hasMore = usersData?.meta?.hasMore ?? false;

  // Filter changes always jump back to the first page.
  const handleFiltersChange = useCallback((next: UserPanelFilters) => {
    setFilters(next);
    setOffset(0);
  }, []);

  const handleUserSelect = (id: string) => {
    const user = rawUsers.find((u) => u.id === id);
    if (!user) return;
    if (selectedUser && selectedUser.id === id) {
      setSelectedUser(null);
      setTimeout(() => navigate(`/user-profile/${id}`), 0);
    } else {
      setSelectedUser(user);
      navigate(`/user-profile/${id}`);
    }
  };

  // Load user from URL parameter on mount or when userId changes
  useEffect(() => {
    if (userId && isAdmin) {
      fetchUserProfile(userId)
        .unwrap()
        .then((result) => setSelectedUser(result))
        .catch((error) => {
          console.error('Failed to fetch user profile:', error);
          setSelectedUser(null);
          navigate('/user-profile');
        });
    } else if (!userId) {
      setSelectedUser(null);
    }
  }, [userId, isAdmin, fetchUserProfile, navigate]);

  // Auto-switch tab from URL query params
  useEffect(() => {
    if (searchParams.get('userConversationId')) setActiveTab('history');
  }, [searchParams]);

  // Creep the progress bar while an operation runs — the endpoints are
  // synchronous and emit no real progress, so this just signals liveness.
  useEffect(() => {
    if (!opLoading) {
      setOpProgress(0);
      return;
    }
    setOpProgress(12);
    const id = setInterval(() => {
      setOpProgress((p) => (p >= 90 ? p : p + (90 - p) * 0.12));
    }, 400);
    return () => clearInterval(id);
  }, [opLoading]);

  // ---- Import / Export helpers ----

  const runImport = async (users: any[]) => {
    setOpInfo({
      title: 'Creating users from exported profiles…',
      subtitle: `Importing ${users.length} profile(s)…`,
    });
    try {
      const result = await importUserProfiles(users).unwrap();
      const renamed = result.created.filter(
        (c: any) => c.originalEmail !== c.newEmail
      );
      const parts = [`Imported ${result.created.length} user(s)`];
      if (renamed.length > 0) parts.push(`${renamed.length} renamed`);
      if (result.skipped.length > 0)
        parts.push(`${result.skipped.length} skipped`);
      toast({
        type: result.skipped.length > 0 ? 'warning' : 'success',
        title: parts.join(', '),
      });
      refetchUsers();
    } catch {
      toast({ type: 'error', title: 'Failed to import profiles' });
    }
  };

  const onImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    let parsed: any;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      toast({
        type: 'error',
        title: 'Invalid file',
        message: 'Could not parse JSON.',
      });
      return;
    }
    const users = Array.isArray(parsed) ? parsed : [parsed];
    if (users.length === 0) {
      toast({
        type: 'warning',
        title: 'Empty file',
        message: 'No users found to import.',
      });
      return;
    }
    if (IS_PROD) {
      setPendingImportUsers(users);
      setImportConfirmOpen(true);
      return;
    }
    await runImport(users);
  };

  const runSampleCreate = async (sampleUsers: any[], images: File[]) => {
    setOpInfo({
      title: 'Creating users from JSON…',
      subtitle: `Importing ${sampleUsers.length} profile(s) and photos…`,
    });
    const formData = new FormData();
    formData.append('users', JSON.stringify(sampleUsers));
    images.forEach((f) => formData.append('photos', f, f.name));
    let result: any;
    try {
      result = await importSampleProfiles(formData).unwrap();
    } catch {
      toast({ type: 'error', title: 'Failed to create users' });
      return;
    }
    const photosUploaded = result.created.reduce(
      (s: number, c: any) => s + (c.photosUploaded ?? 0),
      0
    );
    const photosMissing = result.created.reduce(
      (s: number, c: any) => s + (c.photosMissing ?? 0),
      0
    );
    const parts = [`Created ${result.created.length} user(s)`];
    if (result.skipped.length > 0)
      parts.push(`${result.skipped.length} skipped`);
    parts.push(`${photosUploaded} photo(s) uploaded`);
    if (photosMissing > 0)
      parts.push(`${photosMissing} photo(s) missing/failed`);
    toast({
      type:
        result.skipped.length > 0 || photosMissing > 0 ? 'warning' : 'success',
      title: parts.join(', '),
    });
    refetchUsers();
  };

  const onSampleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    const jsonFiles = files.filter((f) =>
      f.name.toLowerCase().endsWith('.json')
    );
    const images = files.filter((f) => /\.(png|jpe?g|webp|gif)$/i.test(f.name));
    if (jsonFiles.length === 0) {
      toast({
        type: 'error',
        title: 'No JSON file selected',
        message: 'Select the profile JSON file along with photos.',
      });
      return;
    }
    const sampleUsers: any[] = [];
    try {
      for (const jf of jsonFiles) {
        const parsed = JSON.parse(await jf.text());
        if (Array.isArray(parsed)) sampleUsers.push(...parsed);
        else sampleUsers.push(parsed);
      }
    } catch {
      toast({
        type: 'error',
        title: 'Invalid file',
        message: 'Could not parse JSON.',
      });
      return;
    }
    if (sampleUsers.length === 0) {
      toast({
        type: 'warning',
        title: 'Empty file',
        message: 'No users found to create.',
      });
      return;
    }
    if (IS_PROD) {
      setPendingSampleCreate({ users: sampleUsers, images });
      setCreateConfirmOpen(true);
      return;
    }
    await runSampleCreate(sampleUsers, images);
  };

  const userPanelMenu = [
    {
      icon: 'database',
      label: 'Create users from JSON',
      onClick: () => sampleInputRef.current?.click(),
    },
    {
      icon: 'upload',
      label: 'Create users from exported profiles',
      onClick: () => fileInputRef.current?.click(),
    },
  ];

  // Gender string helper (numeric enum → string for Avatar/GenderIcon)
  const genderStr = (g: any): 'female' | 'male' | 'nonbinary' | undefined => {
    if (g === 2) return 'female';
    if (g === 4) return 'male';
    if (g === 8) return 'nonbinary';
    return undefined;
  };

  const typeStr = (t: any): 'normal' | 'sample' | 'test' => {
    if (t === UserType.SAMPLE) return 'sample';
    if (t === UserType.TEST) return 'test';
    return 'normal';
  };

  return (
    // Bug 2 fix: grid is always 320px 1fr — no conditional column widths.
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '320px 1fr',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={onImportFileChange}
      />
      <input
        type="file"
        ref={sampleInputRef}
        multiple
        accept="application/json,.json,image/*"
        style={{ display: 'none' }}
        onChange={onSampleFileChange}
      />

      {/* Left panel — always 320px, bordered, no maxWidth/margin-auto */}
      <div
        style={{
          borderRight: '1px solid var(--border)',
          padding: 12,
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        {error && (
          <Alert color="red" title="Failed to load users">
            {(error as any)?.data?.message ??
              'Could not fetch the user list. Check your connection or try refreshing.'}
          </Alert>
        )}
        {!error && (
          <UserSearchPanel
            users={panelUsers}
            totalCount={totalUsers}
            selectedId={selectedUser?.id}
            onSelect={handleUserSelect}
            title="All Users"
            enableDeleted
            menu={userPanelMenu}
            filterGroups={defaultUserFilterGroups}
            onFiltersChange={handleFiltersChange}
            loading={usersFetching}
            pagination={{
              offset,
              limit: PAGE_SIZE,
              hasMore,
              total: totalUsers,
              onOffsetChange: setOffset,
            }}
          />
        )}
      </div>

      {/* Right pane — placeholder when no user selected, detail when one is */}
      {!selectedUser ? (
        <div
          className="col"
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: 24,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 'var(--radius-xl)',
              background: 'var(--accent-subtle)',
              color: 'var(--accent-text)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 18,
            }}
          >
            <Icon name="user" size={28} />
          </div>
          <div
            style={{
              fontSize: 'var(--fs-xl)',
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}
          >
            No user selected
          </div>
          <p
            className="text-muted"
            style={{
              fontSize: 'var(--fs-md)',
              maxWidth: 340,
              marginTop: 8,
              lineHeight: 1.5,
            }}
          >
            Choose someone from the list on the left to view and edit their
            profile, vector, suggestions, matches, and conversation history.
          </p>
          <div
            className="row gap-2"
            style={{
              marginTop: 20,
              color: 'var(--text-faint)',
              fontSize: 'var(--fs-sm)',
            }}
          >
            <Icon name="sliders-horizontal" size={15} />
            <span>
              {totalUsers ?? panelUsers.filter((u) => !u.deleted).length} users
              &middot; use Filters to narrow the list
            </span>
          </div>
        </div>
      ) : (
        <div className="col" style={{ overflow: 'hidden' }}>
          {/* User header + tab strip */}
          <div
            style={{
              padding: '14px 22px 0',
              borderBottom: '1px solid var(--border)',
              flexShrink: 0,
              background: 'var(--bg-canvas)',
            }}
          >
            <div className="row gap-3" style={{ marginBottom: 12 }}>
              <Avatar
                name={selectedUser.profile?.name || selectedUser.email}
                gender={genderStr(selectedUser.profile?.gender)}
                hue={selectedUser.profile?.hue}
                ring
                size={40}
              />
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="row gap-2">
                  <span
                    className="page-title truncate"
                    style={{ fontSize: 'var(--fs-lg)' }}
                  >
                    {selectedUser.profile?.name || selectedUser.email}
                  </span>
                  <GenderIcon
                    gender={genderStr(selectedUser.profile?.gender)}
                    size={15}
                  />
                  <UserTypeBadge type={typeStr(selectedUser.userType)} />
                </div>
                <div
                  className="text-muted truncate"
                  style={{ fontSize: 'var(--fs-sm)' }}
                >
                  {selectedUser.email}
                  {selectedUser.profile?.city
                    ? ` · ${selectedUser.profile.city}${
                        selectedUser.profile.state
                          ? `, ${selectedUser.profile.state}`
                          : ''
                      }`
                    : ''}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                title="Close"
                onClick={() => {
                  setSelectedUser(null);
                  navigate('/user-profile');
                }}
              >
                <Icon name="x" size={16} />
              </Button>
            </div>

            <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />
          </div>

          {/* Tab content */}
          <div className="grow" style={{ overflow: 'hidden' }}>
            {activeTab === 'profile' && (
              <UserProfileEditor
                selectedUser={selectedUser}
                onUserDeleted={() => {
                  setSelectedUser(null);
                  navigate('/user-profile');
                }}
              />
            )}
            {activeTab === 'public' && (
              <UserPublicProfile userId={selectedUser.id} />
            )}
            {activeTab === 'suggestions' && (
              <UserSuggestions
                userId={selectedUser.id}
                onChatClick={(userConversationId) => {
                  setActiveTab('history');
                  navigate(
                    `/user-profile/${selectedUser.id}?userConversationId=${userConversationId}`,
                    { replace: true }
                  );
                }}
              />
            )}
            {activeTab === 'history' && (
              <UserConversationHistory userId={selectedUser.id} />
            )}
            {activeTab === 'vector' && (
              <UserVectorProfile userId={selectedUser.id} />
            )}
            {activeTab === 'qr' && <UserQrCode selectedUser={selectedUser} />}
          </div>
        </div>
      )}

      {/* Confirm import to production modal */}
      <Modal
        open={importConfirmOpen}
        onClose={() => {
          setImportConfirmOpen(false);
          setPendingImportUsers(null);
        }}
        title="Import to production?"
        width={420}
        footer={
          <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
            <Button
              variant="ghost"
              onClick={() => {
                setImportConfirmOpen(false);
                setPendingImportUsers(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={importUserProfilesStatus.isLoading}
              onClick={async () => {
                const users = pendingImportUsers;
                setImportConfirmOpen(false);
                setPendingImportUsers(null);
                if (users) await runImport(users);
              }}
            >
              Import to prod
            </Button>
          </div>
        }
      >
        <Alert color="red" title="This action affects real users">
          You are about to import{' '}
          <strong>{pendingImportUsers?.length ?? 0}</strong> profile(s) into{' '}
          <strong>PRODUCTION</strong>. This will create or update real user
          records.
        </Alert>
      </Modal>

      {/* Confirm sample-create in production modal */}
      <Modal
        open={createConfirmOpen}
        onClose={() => {
          setCreateConfirmOpen(false);
          setPendingSampleCreate(null);
        }}
        title="Create users in production?"
        width={420}
        footer={
          <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
            <Button
              variant="ghost"
              onClick={() => {
                setCreateConfirmOpen(false);
                setPendingSampleCreate(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={importSampleProfilesStatus.isLoading}
              onClick={async () => {
                const payload = pendingSampleCreate;
                setCreateConfirmOpen(false);
                setPendingSampleCreate(null);
                if (payload)
                  await runSampleCreate(payload.users, payload.images);
              }}
            >
              Create in prod
            </Button>
          </div>
        }
      >
        <Alert color="red" title="This action affects real users">
          You are about to create{' '}
          <strong>{pendingSampleCreate?.users.length ?? 0}</strong> user(s) with
          photos in <strong>PRODUCTION</strong>. This will create real user
          records.
        </Alert>
      </Modal>

      {/* Running indicator for long create/import operations */}
      <ProgressToast
        open={opLoading}
        status="running"
        title={opInfo?.title ?? 'Working…'}
        subtitle={opInfo?.subtitle}
        progress={opProgress}
      />
    </div>
  );
};

export default UserProfilePage;
